/**
 * The perspective camera, and the backdrop layers behind the play plane.
 *
 * ## The shape of this
 *
 * The game is 2D. It is rendered in 3D. Both halves of that are load-bearing.
 * Gameplay stays on the XY plane in pixels, exactly as the simulation has always
 * modelled it; 3D is used for geometry and light, not for movement. That is what
 * a 2.5D platformer is, and it is why the 8,000 lines of simulation needed no
 * changes when this was built.
 *
 * The camera faces the play plane straight on, keeping tile edges square.
 * Extruded props and distant scenery retain perspective and parallax. The numbers that decide where it
 * goes live in `framing.ts`, not here, so they can be tested without a GPU --
 * see the note in that file about why they are not duplicated into the tests.
 *
 * ## Depth layers, back to front
 *
 *   -600  backdrop: theme sky, baked pattern
 *   -300  clouds
 *   -120  hills
 *     0  terrain: a slab whose front face carries the pixel art
 *    +8  placed objects
 *   +16  entities
 *   +24  the player
 *   +60  editor overlay: grid, bounds, placement cursor
 *
 * Parallax is not faked. Every backdrop layer is a real object at a real world
 * position, and the perspective projection does the rest -- which is both
 * simpler and correct, and is why the counter-scrolling that an orthographic
 * camera needed is gone.
 */

import * as THREE from 'three'
import { TILE, VIEW_H, VIEW_W } from '../engine/constants'
import { spriteSize } from '../engine/atlas'
import type { Camera } from '../core/camera'
import type { Level } from '../game/level'
import type { ThemeId } from '../game/types'
import { ENEMY_KIND } from '../game/objects'
import type { PlayerView, Visual } from '../engine/visuals'
import {
  entityVisual,
  objectVisual,
  playerVisual,
  terrainKey,
} from '../engine/visuals'
import {
  anchorToCentre,
  objectFoot,
  foregroundPlacement,
  type Anchor,
} from './anchors'
import { buildModel } from './models'
import { spriteTexture, tintedTexture } from './textures'
import {
  SKY_COVER_MARGIN,
  cameraDistance,
  cameraPlacement,
  cloudRowY,
  frustumCoverAt,
  verticalFovFor,
  worldPad,
  worldRangeAtDepth,
} from './framing'

// ── depth layers ─────────────────────────────────────────────────────────────
const Z_BACKDROP = -600
const Z_CLOUD = -300
const Z_HILL = -120
const Z_TERRAIN = 0
const Z_OBJECT = 8
const Z_ENTITY = 16
const Z_PLAYER = 24
const Z_OVERLAY = 60

/**
 * Cloud and hill layout, in world pixels.
 *
 * A lattice, not a screen-space strip: a cloud's position is a pure function of
 * its lattice index, so it is the same cloud every time the camera passes it.
 * `CLOUD_DRIFT` is a slow wind in world pixels per second, applied to every
 * cloud alike.
 */
const CLOUD_PERIOD = 192
const CLOUD_ROWS = 3
const HILL_PERIOD = 128
const CLOUD_KEYS = ['cloud', 'cloud2'] as const

const THEME_FILL: Record<ThemeId, string> = {
  overworld: '#769ca6',
  sky: '#a3c3c2',
  underground: '#101018',
  night: '#182050',
  castle: '#2a2438',
}

/** Deterministic 0..1 hash. Backgrounds must not use `Math.random`. */
function hash01(n: number): number {
  let h = Math.imul(n + 1, 0x9e3779b1) >>> 0
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b) >>> 0
  h = (h ^ (h >>> 13)) >>> 0
  return (h % 4096) / 4096
}

export interface RenderOptions {
  showGrid?: boolean
  /**
   * Draw hidden blocks as if they had been hit. The editor sets this, because a
   * designer cannot place a block that the view refuses to show; play leaves it
   * off, which is what makes the block hidden.
   */
  revealHidden?: boolean
  editorCursor?: {
    tx: number
    ty: number
    w: number
    h: number
    ok: boolean
  } | null
}

/**
 * A sub-rectangle of the 320x180 buffer to render into, in buffer pixels with
 * Y measured from the top. Null means the whole buffer, which is the game.
 *
 * The editor needs this: its level view is inset by a category rail and a tool
 * strip, and the rest of the buffer belongs to 2D chrome drawn on the layer
 * above. Scissoring the WebGL context to the same rect reproduces the old
 * `ctx.translate(VIEW.x, VIEW.y)`, so the editor's picking arithmetic and its
 * overlay drawing both still line up.
 */
export interface ViewportRect {
  x: number
  y: number
  w: number
  h: number
}

/** A plane's world size, from the sprite it displays. */
function quadSize(key: string): { w: number; h: number } {
  return spriteSize(key)
}

/** Terrain tiles are drawn unrotated, untinted and unoffset, at native size. */
const TERRAIN_CACHE = new Map<string, Visual>()
function terrainVisual(key: string): Visual {
  let v = TERRAIN_CACHE.get(key)
  if (v === undefined) {
    v = {
      key,
      alpha: 1,
      dx: 0,
      dy: 0,
      rot: 0,
      scale: 1,
      tint: null,
    }
    TERRAIN_CACHE.set(key, v)
  }
  return v
}

/**
 * The flat buffers one batch writes its quads into.
 *
 * Positions and uvs are vertex-major, so quad `n` starts at `n * 4`; indices
 * address vertices, so they are relative to that base rather than to the mesh.
 */
export interface QuadTarget {
  /** `quads * 4` vertices, xyz. */
  position: Float32Array
  /** `quads * 4` vertices, uv. */
  uv: Float32Array
  /** `quads * 6` indices, two triangles per quad. */
  index: Uint16Array
}

/**
 * Write one axis-aligned sprite quad into flat arrays at quad slot `offset`.
 *
 * `(cx, cy)` is the sprite's centre in the simulation's world pixels, where Y
 * grows *downward*; Three's Y grows up, so the quad's top edge lands at
 * `-(cy - h/2)`. The layout is the one `PlaneGeometry(1, 1)` produces and the
 * pooled sprite quads use: corners (left,top) (right,top) (left,bottom)
 * (right,bottom), uvs (0,1) (1,1) (0,0) (1,0), indices 0,2,1, 2,3,1.
 *
 * A flipped quad swaps its u coordinates. That is what a `scale.x = -1` on a
 * pooled node does to the art, and it keeps the winding and the footprint
 * unchanged, which mirroring the positions would not.
 */
export function writeQuad(
  target: QuadTarget,
  offset: number,
  cx: number,
  cy: number,
  w: number,
  h: number,
  z: number,
  flip: boolean,
): void {
  const p = offset * 12
  const t = offset * 8
  const i = offset * 6
  const left = cx - w / 2
  const right = cx + w / 2
  const top = -cy + h / 2
  const bottom = -cy - h / 2
  const u0 = flip ? 1 : 0
  const u1 = flip ? 0 : 1

  const position = target.position
  position[p] = left
  position[p + 1] = top
  position[p + 2] = z
  position[p + 3] = right
  position[p + 4] = top
  position[p + 5] = z
  position[p + 6] = left
  position[p + 7] = bottom
  position[p + 8] = z
  position[p + 9] = right
  position[p + 10] = bottom
  position[p + 11] = z

  const uv = target.uv
  uv[t] = u0
  uv[t + 1] = 1
  uv[t + 2] = u1
  uv[t + 3] = 1
  uv[t + 4] = u0
  uv[t + 5] = 0
  uv[t + 6] = u1
  uv[t + 7] = 0

  const base = offset * 4
  const index = target.index
  index[i] = base
  index[i + 1] = base + 2
  index[i + 2] = base + 1
  index[i + 3] = base + 2
  index[i + 4] = base + 3
  index[i + 5] = base + 1
}

/**
 * Quad capacity at which 16-bit vertex indices stop fitting.
 *
 * A course is at most 160x30 tiles, so no batch comes close; the guard exists
 * so a future, larger world fails quietly rather than corrupting indices.
 */
const MAX_QUADS = 16384

function allocateQuads(capacity: number): QuadTarget {
  return {
    position: new Float32Array(capacity * 12),
    uv: new Float32Array(capacity * 8),
    index: new Uint16Array(capacity * 6),
  }
}

function dynamicAttribute(
  array: Float32Array | Uint16Array,
  itemSize: number,
): THREE.BufferAttribute {
  const attribute = new THREE.BufferAttribute(array, itemSize)
  // These buffers are rewritten every frame (or every edit), so tell the
  // driver not to expect a static upload.
  attribute.setUsage(THREE.DynamicDrawUsage)
  return attribute
}

/** Upload only the used prefix; the tail is unused capacity, not content. */
function uploadUsed(attribute: THREE.BufferAttribute, count: number): void {
  attribute.clearUpdateRanges()
  attribute.addUpdateRange(0, count)
  attribute.needsUpdate = true
}

/**
 * One merged mesh for many flat sprite quads sharing a material.
 *
 * A frame is `begin()`, repeated `add()`, then `end()`. Buffers grow by
 * doubling from a small capacity and only the used prefix is uploaded, so a
 * batch that usually holds a handful of quads does not pay to upload a large
 * fixed buffer every frame.
 */
class QuadBatch {
  readonly mesh: THREE.Mesh
  private readonly geometry = new THREE.BufferGeometry()
  private target: QuadTarget
  private position: THREE.BufferAttribute
  private uv: THREE.BufferAttribute
  private index: THREE.BufferAttribute
  private capacity: number
  private count = 0

  constructor(material: THREE.Material, capacity = 32) {
    this.capacity = capacity
    this.target = allocateQuads(capacity)
    this.position = dynamicAttribute(this.target.position, 3)
    this.uv = dynamicAttribute(this.target.uv, 2)
    this.index = dynamicAttribute(this.target.index, 1)
    this.geometry.setAttribute('position', this.position)
    this.geometry.setAttribute('uv', this.uv)
    this.geometry.setIndex(this.index)
    this.geometry.setDrawRange(0, 0)
    this.mesh = new THREE.Mesh(this.geometry, material)
    // A batch spans a whole layer, so its bounding volume describes nothing
    // useful and culling it per frame is pure overhead -- and it would read
    // the unused capacity tail as geometry.
    this.mesh.frustumCulled = false
    this.mesh.visible = false
  }

  /** Drop this frame's quads; the capacity is kept for the next one. */
  begin(): void {
    this.count = 0
  }

  /** Add one quad centred at `(cx, cy)`, world pixels with Y down. */
  add(
    cx: number,
    cy: number,
    w: number,
    h: number,
    z: number,
    flip = false,
  ): void {
    if (this.count === this.capacity && this.capacity < MAX_QUADS) this.grow()
    if (this.count >= MAX_QUADS) return
    writeQuad(this.target, this.count, cx, cy, w, h, z, flip)
    this.count++
  }

  /** Publish the quads written since `begin()`. */
  end(): void {
    this.geometry.setDrawRange(0, this.count * 6)
    this.mesh.visible = this.count > 0
    if (this.count === 0) return
    uploadUsed(this.position, this.count * 12)
    uploadUsed(this.uv, this.count * 8)
    uploadUsed(this.index, this.count * 6)
  }

  private grow(): void {
    const capacity = Math.min(this.capacity * 2, MAX_QUADS)
    const target = allocateQuads(capacity)
    target.position.set(this.target.position)
    target.uv.set(this.target.uv)
    target.index.set(this.target.index)
    this.capacity = capacity
    this.target = target
    this.position = dynamicAttribute(target.position, 3)
    this.uv = dynamicAttribute(target.uv, 2)
    this.index = dynamicAttribute(target.index, 1)
    this.geometry.setAttribute('position', this.position)
    this.geometry.setAttribute('uv', this.uv)
    this.geometry.setIndex(this.index)
  }
}

/**
 * A model's parts, instanced across many placements.
 *
 * Terrain tiles with real geometry cannot be replaced by flat quads without
 * changing the picture. The slab behind the art is visible at the edges of a
 * tile -- the side, top and bottom faces are the seams and lips that read as
 * depth -- and `platform`'s slab even shows through the transparent half of its
 * sprite. Instancing keeps exactly the geometry the pooled nodes drew, still
 * collapsing every visible tile of a key into one draw call per model part.
 *
 * One instance matrix per part per placement, so a pass of `n` tiles costs
 * `n * 16` floats per part and a single draw call each.
 */
class ModelBatch {
  readonly meshes: THREE.InstancedMesh[] = []
  private readonly parts: {
    geometry: THREE.BufferGeometry
    material: THREE.Material
    local: THREE.Matrix4
  }[] = []
  private readonly scratch = new THREE.Matrix4()
  private capacity = 0
  private used = 0

  constructor(
    model: THREE.Object3D,
    capacity: number,
    private readonly parent: THREE.Object3D,
  ) {
    model.updateMatrixWorld(true)
    model.traverse((child) => {
      const mesh = child as THREE.Mesh
      if (mesh.isMesh !== true) return
      this.parts.push({
        geometry: mesh.geometry,
        material: (Array.isArray(mesh.material)
          ? mesh.material[0]
          : mesh.material) as THREE.Material,
        local: mesh.matrixWorld.clone(),
      })
    })
    this.allocate(capacity)
  }

  /** Drop this pass's placements; the capacity is kept for the next one. */
  begin(): void {
    this.used = 0
  }

  /** Place the model's parts at a model-node position, world Y already negated. */
  add(x: number, y: number, z: number): void {
    if (this.used === this.capacity) this.allocate(this.capacity * 2)
    for (let i = 0; i < this.meshes.length; i++) {
      this.scratch.makeTranslation(x, y, z).multiply(this.parts[i]!.local)
      this.meshes[i]!.setMatrixAt(this.used, this.scratch)
    }
    this.used++
  }

  /** Publish the placements written since `begin()`. */
  end(): void {
    for (const mesh of this.meshes) {
      mesh.count = this.used
      mesh.visible = this.used > 0
      if (this.used === 0) continue
      // Only the used prefix is uploaded; the tail is spare capacity.
      const attribute = mesh.instanceMatrix
      attribute.clearUpdateRanges()
      attribute.addUpdateRange(0, this.used * 16)
      attribute.needsUpdate = true
    }
  }

  private allocate(capacity: number): void {
    const next: THREE.InstancedMesh[] = []
    for (const part of this.parts) {
      const mesh = new THREE.InstancedMesh(
        part.geometry,
        part.material,
        capacity,
      )
      // A batch is refilled from the visible range, so its bounds would have
      // to be recomputed per frame anyway; skip the test instead.
      mesh.frustumCulled = false
      mesh.count = 0
      mesh.visible = false
      this.parent.add(mesh)
      next.push(mesh)
    }
    // Growing must not lose the placements already written this pass.
    for (let i = 0; i < this.meshes.length; i++) {
      const old = this.meshes[i]!
      next[i]!.instanceMatrix.array.set(old.instanceMatrix.array)
      old.removeFromParent()
      old.dispose()
    }
    this.meshes.length = 0
    for (const mesh of next) this.meshes.push(mesh)
    this.capacity = capacity
  }
}

export class Renderer3D {
  private readonly renderer: THREE.WebGLRenderer
  private readonly scene = new THREE.Scene()
  private readonly camera: THREE.PerspectiveCamera

  private readonly backdrop: THREE.Group
  private readonly clouds: THREE.Group
  private readonly hills: THREE.Group
  private readonly terrain: THREE.Group
  private readonly objects: THREE.Group
  private readonly entities: THREE.Group
  private readonly actors: THREE.Group
  private readonly overlay: THREE.Group

  /** Pooled nodes, keyed by everything that decides their geometry. */
  private readonly pools = new Map<string, THREE.Object3D[]>()
  private readonly cursors = new Map<string, number>()

  /**
   * Merged terrain, one batch per key: instanced model parts for tiles with
   * geometry, flat quads for the rest. All of them are refilled every frame
   * from the culled tile range.
   */
  private readonly terrainQuads = new Map<string, QuadBatch>()
  private readonly terrainModels = new Map<string, ModelBatch>()
  private readonly cloudQuads = new Map<string, QuadBatch>()
  private readonly hillQuads = new Map<string, QuadBatch>()
  private readonly objectQuads = new Map<string, QuadBatch>()

  /** Materials owned by the parallax batches, which no shared cache holds. */
  private readonly parallaxMaterials: THREE.Material[] = []

  private readonly grid: THREE.LineSegments
  private readonly cursorBox: THREE.LineSegments
  private readonly cursorFill: THREE.Mesh

  private viewport: ViewportRect | null = null
  private viewW = VIEW_W
  private viewH = VIEW_H
  /** World y the hills sit on: the level's floor. */
  private hillBaseY = 0

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    })
    this.renderer.setPixelRatio(4)
    this.renderer.setSize(VIEW_W, VIEW_H, false)
    this.renderer.setClearColor(0x000000, 1)

    this.camera = new THREE.PerspectiveCamera(1, VIEW_W / VIEW_H, 1, 4000)

    // Lighting only reaches the modelled props: every flat face and every
    // textured quad uses an unlit material, so the authored colours survive
    // whatever these are set to. Only the sides, rims and extruded surfaces are
    // lit, and those are all mid-to-dark base colours chosen to sit correctly
    // under this much light.
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.5))
    const key = new THREE.DirectionalLight(0xffffff, 0.6)
    key.position.set(-1, 1.4, 2.2)
    this.scene.add(key)

    this.backdrop = new THREE.Group()
    this.clouds = new THREE.Group()
    this.hills = new THREE.Group()
    this.terrain = new THREE.Group()
    this.objects = new THREE.Group()
    this.entities = new THREE.Group()
    this.actors = new THREE.Group()
    this.overlay = new THREE.Group()
    for (const g of [
      this.backdrop,
      this.clouds,
      this.hills,
      this.terrain,
      this.objects,
      this.entities,
      this.actors,
      this.overlay,
    ]) {
      this.scene.add(g)
    }

    this.grid = makeLineSegments(0xffffff, 0.18)
    this.grid.visible = false
    this.overlay.add(this.grid)

    this.cursorBox = makeLineSegments(0x00e800, 1)
    this.cursorFill = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        color: 0x00e800,
        transparent: true,
        opacity: 0.15,
        depthTest: false,
      }),
    )
    this.cursorFill.visible = false
    this.cursorBox.visible = false
    this.overlay.add(this.cursorFill, this.cursorBox)
  }

  // ── pooling ───────────────────────────────────────────────────────────────

  private beginFrame(): void {
    for (const key of this.pools.keys()) this.cursors.set(key, 0)
  }

  private endFrame(): void {
    for (const [key, pool] of this.pools) {
      const used = this.cursors.get(key) ?? 0
      for (let i = used; i < pool.length; i++) {
        const node = pool[i]
        if (node !== undefined) node.visible = false
      }
    }
  }

  /**
   * A node for this frame, reusing a pooled one when the signature matches.
   *
   * The signature must capture everything that changes the node's geometry and
   * material, or a reused node renders the previous frame's prop. It must *also*
   * name the group, because pools are shared across groups and the same
   * signature legitimately appears in two of them: `model|brick` is both a
   * terrain tile and a placed object, and `quad|hill-lg` is drawn in the hills
   * group while a hillside prop with the same art is drawn in objects.
   *
   * That second point was a live bug. Without the group in the key, a hillside
   * object could be handed a node parented to the hills group, which then
   * scrolled with the parallax layer instead of staying put -- a scenery prop
   * drifting across the level, from a pool index collision rather than any
   * mistake in the placement maths.
   */
  private acquire(
    signature: string,
    parent: THREE.Object3D,
    factory: () => THREE.Object3D,
  ): THREE.Object3D {
    let pool = this.pools.get(signature)
    if (pool === undefined) {
      pool = []
      this.pools.set(signature, pool)
    }
    const i = this.cursors.get(signature) ?? 0
    this.cursors.set(signature, i + 1)

    let node = pool[i]
    if (node === undefined) {
      node = factory()
      node.userData.signature = signature
      parent.add(node)
      pool[i] = node
    }
    node.visible = true
    return node
  }

  /**
   * A pool key that cannot collide across groups.
   *
   * Scoped by the group's own identity, so two groups drawing the same art get
   * separate pools rather than sharing a slot list.
   */
  private scoped(group: THREE.Object3D, rest: string): string {
    return `${group.uuid}|${rest}`
  }

  // ── materials ──────────────────────────────────────────────────────────────

  private readonly materials = new Map<string, THREE.Material>()

  /**
   * An unlit material for a sprite quad. Unlit is the point: the art was drawn
   * with its own shading, and running it through the lighting model would double
   * up and mud every colour.
   */
  private quadMaterial(
    key: string,
    flip: boolean,
    tint: string | null,
  ): THREE.Material {
    const cacheKey = `${key}|${flip ? 1 : 0}|${tint ?? ''}`
    const hit = this.materials.get(cacheKey)
    if (hit !== undefined) return hit
    const mat = new THREE.MeshBasicMaterial({
      map: tint === null ? spriteTexture(key) : tintedTexture(key, tint),
      transparent: true,
      // Pixel art has hard edges, so cut the fully transparent pixels out of the
      // depth and sort pass entirely instead of blending them.
      alphaTest: 0.5,
      side: THREE.DoubleSide,
    })
    this.materials.set(cacheKey, mat)
    return mat
  }

  private readonly geometries = new Map<string, THREE.BufferGeometry>()

  private plane(w: number, h: number): THREE.BufferGeometry {
    const key = `${w}x${h}`
    let geo = this.geometries.get(key)
    if (geo === undefined) {
      geo = new THREE.PlaneGeometry(w, h)
      this.geometries.set(key, geo)
    }
    return geo
  }

  // ── placing a resolved visual ──────────────────────────────────────────────

  /**
   * Place one visual. Prefers real geometry, falls back to a textured quad.
   *
   * ## The anchor
   *
   * Both branches centre their geometry on the object's own origin, so the
   * caller's anchor has to be converted to a *centre* first. There are two
   * conventions and holding it to two is the point:
   *
   *   - `centre`: the anchor already is the centre. Entities, because a 14x14
   *     hitbox sits inside a 16x16 sprite and the art is centred on the box.
   *   - `foot`: the anchor is the bottom edge, so the centre is half a sprite
   *     above it. Objects and the player, which stand on something.
   *
   * There was a third convention, `tileBase`, and *it* was the bug. Three ways
   * to say "where is this vertically" meant four call sites could each pick one,
   * and if they disagreed the art drifted with no error anywhere -- which is
   * exactly the reported symptom of actors sitting a tile below the floor. It
   * was also arithmetically a no-op for the 1x1 majority, so the common case
   * looked right while the tall cases were wrong.
   *
   * `quads`, when given, is where flat visuals go instead of taking a pooled
   * node of their own. Modelled art, rotation and partial opacity still need a
   * node: a batch writes positions once and shares one material, so it cannot
   * express either. Omitting `quads` keeps the pooled path for everything.
   */
  private place(
    v: Visual,
    cx: number,
    anchorY: number,
    anchor: Anchor,
    z: number,
    parent: THREE.Object3D,
    flip: boolean,
    tick: number,
    quads?: Map<string, QuadBatch>,
  ): void {
    const modelled = buildModel(v.key)
    const size = quadSize(v.key)
    const h = Math.max(1, Math.round(size.h * v.scale))
    const w = Math.max(1, Math.round(size.w * v.scale))
    const centreY = anchorToCentre(anchorY, h, anchor)

    if (modelled !== null) {
      const node = this.acquire(
        this.scoped(parent, `model|${v.key}`),
        parent,
        () => modelled,
      )
      node.position.set(cx + v.dx, -(centreY + v.dy), z)
      node.scale.set((flip ? -1 : 1) * v.scale, v.scale, v.scale)
      this.animate(node, v, tick)
      return
    }

    if (quads !== undefined && v.rot === 0 && v.alpha === 1) {
      this.batchFor(quads, parent, v.key, flip, v.tint, z).add(
        cx + v.dx,
        centreY + v.dy,
        w,
        h,
        z,
        flip,
      )
      return
    }

    const tintKey = v.tint
    const sig = this.scoped(
      parent,
      `quad|${v.key}|${flip ? 1 : 0}|${tintKey ?? ''}`,
    )
    const node = this.acquire(
      sig,
      parent,
      () =>
        new THREE.Mesh(
          new THREE.PlaneGeometry(1, 1),
          this.quadMaterial(v.key, flip, tintKey),
        ),
    )
    const mesh = node as THREE.Mesh
    if (mesh.geometry !== this.plane(w, h)) {
      mesh.geometry = this.plane(w, h)
      mesh.material = this.quadMaterial(v.key, flip, tintKey)
    }
    mesh.position.set(cx + v.dx, -(centreY + v.dy), z)
    mesh.scale.set(flip ? -1 : 1, 1, 1)
    mesh.rotation.z = -v.rot
    ;(mesh.material as THREE.MeshBasicMaterial).opacity = v.alpha
    mesh.renderOrder = z
  }

  /**
   * The batch a flat visual belongs to, created on first use.
   *
   * Keyed like the pool signature -- art, flip, tint -- plus the layer's z,
   * because a batch mesh carries one renderOrder and every quad in it must
   * share a layer for the transparent pass to sort them as they sorted before.
   */
  private batchFor(
    map: Map<string, QuadBatch>,
    parent: THREE.Object3D,
    key: string,
    flip: boolean,
    tint: string | null,
    z: number,
  ): QuadBatch {
    const signature = `${key}|${flip ? 1 : 0}|${tint ?? ''}|${z}`
    let batch = map.get(signature)
    if (batch === undefined) {
      batch = new QuadBatch(this.quadMaterial(key, flip, tint))
      batch.mesh.renderOrder = z
      parent.add(batch.mesh)
      map.set(signature, batch)
    }
    return batch
  }

  /**
   * Per-frame motion for modelled props.
   *
   * ## Why the coin rotates about Y and not Z
   *
   * It has to be Y. A cylinder's axis is Y in Three.js, and the coin's faces
   * point along Z so they meet the camera. Turning it about Z -- what "spin a
   * coin" suggests in a 2D-facing scene -- rotates the disc *in its own plane*,
   * so it looks like a wheel rolling forward and the edge never shows. Turning
   * about Y sweeps the face through edge-on, which is the motion a coin makes
   * and the reason to model it at all.
   *
   * ## Why the phase comes from the tick
   *
   * `rotation.y += step` accumulates on a *pooled* node, and the pool hands out
   * whichever node it has free. That made every coin spin at a rate set by how
   * many other coins shared its node that frame, and made the animation
   * non-reproducible: the same course rendered twice gave different coin phases.
   * Deriving the angle from the level tick makes it a pure function of game
   * state.
   *
   */
  private animate(node: THREE.Object3D, v: Visual, tick: number): void {
    const key = v.key
    if (key === 'coin' || key === 'coin-pop') {
      // A little under one turn per second: fast enough to read as spinning,
      // slow enough that the face is legible more often than the edge.
      node.rotation.y = (tick / 60) * Math.PI * 2 * 0.85
      // The cylinder geometry supplies the changing silhouette as it rotates.
    } else if (key === 'prism') {
      node.rotation.y = (tick / 60) * Math.PI * 2 * 0.6
      node.rotation.z = v.rot
    } else if (key === 'saw' || key === 'saw-h') {
      node.rotation.z = v.rot
    }
  }

  // ── the frame ──────────────────────────────────────────────────────────────

  renderLevel(level: Level, cam: Camera, opts: RenderOptions = {}): void {
    this.beginFrame()
    this.syncTheme(level.theme)

    const rect = this.viewport
    this.viewW = rect === null ? VIEW_W : rect.w
    this.viewH = rect === null ? VIEW_H : rect.h
    this.applyViewport(rect)
    this.aimCamera(cam)
    this.hillBaseY = level.height * TILE

    this.drawBackdrop(cam, level.theme, level.tick)
    this.drawTerrain(level, cam)
    this.drawObjects(level, cam, opts)
    this.drawEntities(level, cam)
    this.drawPlayer(level)
    this.drawOverlay(cam, opts)

    this.endFrame()
    this.renderer.render(this.scene, this.camera)
  }

  /**
   * Aim the side-on camera at the world's view centre.
   *
   * The placement comes from `framing.cameraPlacement`, which is where the
   * Y-flip and the pitch live. This used to inline that arithmetic, and inlined
   * arithmetic is where the sign error came from: negating the look-at target's
   * Y a second time pointed the camera at empty space, and every projected
   * coordinate landed thousands of pixels off screen. Nothing threw, and every
   * mesh had a perfectly sane world position -- it was only visible once the
   * projection was measured. Keeping the numbers in one tested module is the
   * fix, not a comment asking the next person to be careful.
   */
  private aimCamera(cam: Camera): void {
    // The camera frames *the rect being rendered*, not the whole buffer.
    //
    // The editor's level view is inset by a category rail and a tool strip, so
    // framing the full 320x180 and then scissoring to the inset rect shows only
    // the middle of the scene, stretched: the level appeared as a trapezoid with
    // the backdrop filling the corners, and the editor looked broken in a way that
    // no amount of checking the *game* view would have found.
    //
    // `cameraPlacement` takes the rect's own size, so the framing is the art
    // viewport whichever rect is in play, and the editor and the game cannot
    // disagree about what "the view" means.
    const p = cameraPlacement(cam.x, cam.y, this.viewW, this.viewH)
    this.camera.aspect = this.viewW / this.viewH
    this.camera.fov = verticalFovFor(this.viewH)
    this.camera.updateProjectionMatrix()
    this.camera.position.set(p.x, p.y, p.z)
    this.camera.lookAt(new THREE.Vector3(p.aimX, p.aimY, 0))
  }

  /**
   * How much wider than the view the visible world is, in world pixels.
   *
   * Zero at the play plane, where the camera is focused. Terrain is culled
   * against the view rectangle and the pool releases whatever is not drawn, so
   * there is nothing to reveal beyond it.
   */
  private get cullPad(): { x: number; y: number } {
    return worldPad(0, this.viewH)
  }

  /**
   * Terrain as per-key batches, rebuilt from the culled tile range each frame.
   *
   * The range is the same view rectangle the per-tile nodes were placed from,
   * so only visible tiles are ever submitted. That matters because a modelled
   * tile is a slab, not a two-triangle card: a level's worth of them is many
   * thousand triangles, most of which would be clipped away. Building is cheap
   * (a few hundred quads of array writes), so a per-frame rebuild also makes an
   * editor stroke or an On/Off block's rewritten tiles appear with no change
   * counter to go stale.
   */
  private drawTerrain(level: Level, cam: Camera): void {
    const pad = this.cullPad
    const tx0 = Math.max(0, Math.floor((cam.x - pad.x) / TILE))
    const ty0 = Math.max(0, Math.floor((cam.y - pad.y) / TILE))
    const tx1 = Math.min(
      level.width - 1,
      Math.floor((cam.x + this.viewW + pad.x) / TILE),
    )
    const ty1 = Math.min(
      level.height - 1,
      Math.floor((cam.y + this.viewH + pad.y) / TILE),
    )

    for (const batch of this.terrainQuads.values()) batch.begin()
    for (const batch of this.terrainModels.values()) batch.begin()
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        const key = terrainKey(
          level.terrainAt(tx, ty),
          level.terrainAt(tx, ty - 1),
        )
        if (key === null) continue
        const cx = tx * TILE + TILE / 2
        const cy = ty * TILE + TILE / 2
        const quad = this.terrainQuads.get(key)
        if (quad !== undefined) {
          quad.add(cx, cy, TILE, TILE, Z_TERRAIN)
          continue
        }
        let model = this.terrainModels.get(key)
        if (model === undefined) {
          const built = buildModel(key)
          if (built === null) {
            // First sight of a flat key: make its batch and use it now.
            const created = new QuadBatch(this.quadMaterial(key, false, null))
            created.mesh.renderOrder = Z_TERRAIN
            this.terrain.add(created.mesh)
            this.terrainQuads.set(key, created)
            created.add(cx, cy, TILE, TILE, Z_TERRAIN)
            continue
          }
          model = new ModelBatch(built, 32, this.terrain)
          this.terrainModels.set(key, model)
        }
        model.add(cx, -cy, Z_TERRAIN)
      }
    }
    for (const batch of this.terrainQuads.values()) batch.end()
    for (const batch of this.terrainModels.values()) batch.end()
  }

  /**
   * Placed objects.
   *
   * Flat art -- pipe cells, scenery, pickups -- goes through one batch per
   * material. Modelled props, tilts and partial opacity keep their pooled nodes,
   * because a batch writes plain unrotated quads once. Objects change between
   * frames (a spent block, a collected pickup, a moving platform's offset), so
   * these batches are refilled every frame; each uploads only the prefix it
   * used, so the cost tracks the number of objects, not the capacity.
   *
   * Objects outside the view are skipped, as the renderer's own frustum cull
   * used to do. It also keeps a batch for a distant prop's art from being drawn
   * at all, which is where batching would otherwise cost more calls than the
   * pooled nodes did: one mesh per material, not per object.
   */
  private drawObjects(level: Level, cam: Camera, opts: RenderOptions): void {
    const pad = TILE * 6
    const x0 = cam.x - pad
    const x1 = cam.x + this.viewW + pad
    const y0 = cam.y - pad
    const y1 = cam.y + this.viewH + pad

    for (const batch of this.objectQuads.values()) batch.begin()
    for (const o of level.objects) {
      // Enemy placements are drawn by `drawEntities`, from the live entity they
      // spawned: drawing both would leave an inert copy standing next to the
      // walker. Which means an enemy placement with no entity is not drawn at
      // all — so `Level.addObject` has to bring a newly placed enemy to life,
      // or the editor's brush lays down enemies that never appear.
      if (!o.alive || ENEMY_KIND[o.def.id] !== undefined) continue
      const left = o.tx * TILE + o.ox
      const top = o.ty * TILE + o.oy
      if (
        left > x1 ||
        left + o.def.w * TILE < x0 ||
        top > y1 ||
        top + o.def.h * TILE < y0
      ) {
        continue
      }
      if (o.def.id === 'pipe' || o.def.resize === 'width') {
        for (let row = 0; row < o.def.h; row++) {
          for (let col = 0; col < o.def.w; col++) {
            const key =
              o.def.id === 'pipe'
                ? `pipe-${row === 0 ? 'top' : 'body'}-${col === 0 ? 'left' : 'right'}`
                : o.def.sprite === 'moving-platform'
                  ? 'platform'
                  : o.def.sprite
            this.place(
              terrainVisual(key),
              (o.tx + col + 0.5) * TILE + o.ox,
              (o.ty + row + 0.5) * TILE + o.oy,
              'centre',
              0,
              this.objects,
              false,
              level.tick,
              this.objectQuads,
            )
          }
        }
        continue
      }
      const v = objectVisual(o, level, {
        revealHidden: opts.revealHidden === true,
      })
      if (v === null) continue
      // Centre on the footprint, anchor the sprite's feet to the footprint's
      // bottom. Using `def.h` rather than a single tile is what keeps a 2-tall
      // pipe standing on the ground rather than hovering a tile above it.
      const cx = o.tx * TILE + o.ox + (o.def.w * TILE) / 2
      const footY = objectFoot(o.ty, o.def.h, o.oy)
      this.place(
        v,
        cx,
        footY,
        'foot',
        Z_OBJECT,
        this.objects,
        false,
        level.tick,
        this.objectQuads,
      )
    }
    for (const batch of this.objectQuads.values()) batch.end()
  }

  private drawEntities(level: Level, cam: Camera): void {
    for (const e of level.entities) {
      if (!e.alive) continue
      if (e.x + e.w < cam.x - TILE * 2 || e.x > cam.x + this.viewW + TILE * 2)
        continue
      if (e.y + e.h < cam.y - TILE * 2 || e.y > cam.y + this.viewH + TILE * 2)
        continue

      if (e.kind === 'poof') {
        this.drawPoof(e.x + e.w / 2, e.y + e.h / 2, e.data)
        continue
      }
      const v = entityVisual(e, level.tick)
      if (v === null) continue
      this.place(
        v,
        e.x + e.w / 2,
        e.y + e.h / 2,
        'centre',
        Z_ENTITY,
        this.entities,
        e.facing < 0,
        level.tick,
      )
    }
  }

  /** A puff of smoke: an expanding translucent disc, no sprite involved. */
  private drawPoof(
    cx: number,
    cy: number,
    data: Record<string, unknown>,
  ): void {
    const t = clamp01(typeof data.life === 'number' ? data.life : 0.5)
    const node = this.acquire(
      this.scoped(this.entities, 'poof'),
      this.entities,
      () =>
        new THREE.Mesh(
          new THREE.CircleGeometry(1, 12),
          new THREE.MeshBasicMaterial({
            color: 0xfcfcfc,
            transparent: true,
            depthWrite: false,
          }),
        ),
    )
    const mesh = node as THREE.Mesh
    const r = 1 + t * 9
    mesh.position.set(cx, -cy, Z_ENTITY)
    mesh.scale.set(r, r, r)
    ;(mesh.material as THREE.MeshBasicMaterial).opacity = 1 - t
  }

  private drawPlayer(level: Level): void {
    const p = level.player as PlayerView | undefined
    if (p === undefined || p.alive === false) {
      this.playerNode = null
      return
    }
    const resolved = playerVisual(p, level.tick)
    if (resolved === null) {
      this.hidePlayer()
      return
    }

    const v = resolved.visual
    const size = quadSize(v.key)
    const modelled = buildModel(v.key)
    const sig = this.scoped(
      this.actors,
      `player|${v.key}|${p.facing < 0 ? 1 : 0}|${v.tint ?? ''}`,
    )
    // Feet anchor, so the centre is half a sprite above the bottom of the
    // hitbox. The previous code put the plane's *centre* on the feet, which drew
    // Tavi half sunk into the floor -- the clearest symptom of the anchor
    // mix-up, because he is the thing you look at most.
    const centreY = p.y + p.h - (size.h * resolved.squash) / 2
    // Keep the sprite aligned to the collision plane despite its foreground Z.
    const placement = foregroundPlacement(
      p.x + p.w / 2,
      -centreY,
      Z_PLAYER,
      this.camera.position,
    )
    const depthScale = placement.scale
    const playerX = placement.x
    const playerY = placement.y

    let node: THREE.Object3D
    if (modelled !== null) {
      node = this.acquire(sig, this.actors, () => modelled)
    } else {
      node = this.acquire(
        sig,
        this.actors,
        () =>
          new THREE.Mesh(
            new THREE.PlaneGeometry(1, 1),
            this.quadMaterial(v.key, p.facing < 0, v.tint),
          ),
      )
      const mesh = node as THREE.Mesh
      const w = Math.max(1, Math.round(size.w))
      const h = Math.max(1, Math.round(size.h))
      mesh.geometry = this.plane(w, h)
      mesh.material = this.quadMaterial(v.key, p.facing < 0, v.tint)
      mesh.position.set(playerX, playerY, Z_PLAYER)
      mesh.scale.set(
        (p.facing < 0 ? -1 : 1) * depthScale,
        resolved.squash * depthScale,
        depthScale,
      )
      mesh.rotation.z = 0
      mesh.renderOrder = Z_PLAYER
      this.playerNode = mesh
      return
    }

    node.position.set(playerX, playerY, Z_PLAYER)
    node.scale.set(
      (p.facing < 0 ? -1 : 1) * depthScale,
      resolved.squash * depthScale,
      depthScale,
    )
    this.playerNode = node
  }

  private playerNode: THREE.Object3D | null = null

  private hidePlayer(): void {
    if (this.playerNode !== null) this.playerNode.visible = false
    this.playerNode = null
  }

  // ── background ─────────────────────────────────────────────────────────────

  private backdropTexture: THREE.Texture | null = null
  private backdropTheme: ThemeId | null = null

  private syncTheme(theme: ThemeId): void {
    if (this.backdropTheme === theme) return
    this.backdropTheme = theme

    const canvas = document.createElement('canvas')
    canvas.width = VIEW_W
    canvas.height = VIEW_H
    const ctx = canvas.getContext('2d')
    if (ctx !== null) {
      ctx.fillStyle = THEME_FILL[theme] ?? THEME_FILL.overworld
      ctx.fillRect(0, 0, VIEW_W, VIEW_H)
      if (theme === 'underground' || theme === 'castle') {
        const grad = ctx.createLinearGradient(0, 0, 0, VIEW_H)
        if (theme === 'underground') {
          grad.addColorStop(0, '#181822')
          grad.addColorStop(1, '#0a0a10')
        } else {
          grad.addColorStop(0, '#332b44')
          grad.addColorStop(1, '#1d1828')
        }
        ctx.fillStyle = grad
        ctx.fillRect(0, 0, VIEW_W, VIEW_H)
        this.bakeBrick(
          ctx,
          theme === 'castle' ? '#cfc0e0' : '#fcfcfc',
          theme === 'castle' ? 0.16 : 0.05,
        )
      } else if (theme === 'night') {
        for (let i = 0; i < 44; i++) {
          ctx.fillStyle = hash01(i) < 0.5 ? '#fcfcfc' : '#a4a4c8'
          ctx.globalAlpha = 0.8
          ctx.fillRect(
            Math.floor(hash01(i) * VIEW_W),
            Math.floor(hash01(i + 77) * VIEW_H),
            1,
            1,
          )
        }
        ctx.globalAlpha = 1
      }
    }

    this.backdropTexture?.dispose()
    const tex = new THREE.CanvasTexture(canvas)
    tex.magFilter = THREE.NearestFilter
    tex.minFilter = THREE.NearestFilter
    tex.generateMipmaps = false
    tex.colorSpace = THREE.SRGBColorSpace
    this.backdropTexture = tex

    while (this.backdrop.children.length > 0) {
      const child = this.backdrop.children[0]
      if (child === undefined) break
      this.backdrop.remove(child)
    }
    const quad = new THREE.Mesh(
      new THREE.PlaneGeometry(VIEW_W, VIEW_H),
      new THREE.MeshBasicMaterial({ map: tex, depthWrite: false }),
    )
    quad.renderOrder = -1000
    this.backdrop.add(quad)
  }

  /** The masonry grid, baked into the backdrop rather than drawn per frame. */
  private bakeBrick(
    ctx: CanvasRenderingContext2D,
    colour: string,
    alpha: number,
  ): void {
    ctx.save()
    ctx.globalAlpha = alpha
    ctx.fillStyle = colour
    const rowH = 8
    const colW = 16
    for (let row = 0; row * rowH < VIEW_H; row++) {
      const stagger = row % 2 === 0 ? 0 : colW / 2
      for (let wx = -colW; wx < VIEW_W + colW; wx += colW) {
        const x = Math.floor(wx + stagger)
        const y = row * rowH
        ctx.fillRect(x, y, 1, rowH)
        ctx.fillRect(x, y + rowH - 1, colW, 1)
      }
    }
    ctx.restore()
  }

  private drawBackdrop(cam: Camera, theme: ThemeId, tick: number): void {
    // The sky must be placed where the camera's forward ray crosses its own depth,
    // and scaled to cover the whole frustum there. Both halves were wrong on the
    // first perspective attempt and the two failures looked like different bugs:
    // centring on the view left a band of black across the lower screen, and
    // sizing to the view left black wedges in the corners. Neither was visible in
    // the scene graph -- position and scale were both plausible numbers.
    this.placeSkyAtDepth(Z_BACKDROP)
    this.drawClouds(cam, theme, tick)
    this.drawHills(cam)
  }

  private placeSkyAtDepth(z: number): void {
    const ray = this.camera.getWorldDirection(new THREE.Vector3())
    // Solve `position + ray * t = z` for t. Dividing the camera's *own* z by
    // `-ray.z` instead solves for t where z becomes **0**, which parks the sky on
    // the play plane rather than behind it. It looked right, because the quad is
    // opaque and drawn first, but it was in the wrong place.
    const t = (z - this.camera.position.z) / ray.z
    const centre = this.camera.position.clone().addScaledVector(ray, t)
    const cover =
      frustumCoverAt(z, cameraDistance(this.viewH)) * SKY_COVER_MARGIN
    this.backdrop.position.set(centre.x, centre.y, z)
    this.backdrop.scale.set(cover, cover, 1)
  }

  /**
   * Clouds as real objects on their own depth plane.
   *
   * Positions come from a deterministic hash over a *world* lattice, not from the
   * camera. That is the trick: with a perspective camera a cloud at a fixed world
   * x and z parallaxes correctly and continuously as the camera moves, with no
   * scroll factor at all. The first version multiplied `cam.x` by a factor and
   * wrapped it, which under perspective fought the projection and jittered.
   *
   * The vertical band follows the camera, not the level. A course is 30 tiles
   * tall while the camera sees about 180px of it, so any level-relative band
   * puts most of the clouds outside the viewport -- which is what produced an
   * empty sky with nothing to explain it.
   */
  private drawClouds(cam: Camera, theme: ThemeId, tick: number): void {
    for (const batch of this.cloudQuads.values()) batch.begin()
    const show = theme === 'sky' || theme === 'overworld' || theme === 'night'
    if (show) {
      // A slow independent drift, so the sky is alive when the player stands
      // still. Small enough not to read as the clouds being nailed to the world.
      const drift = (tick / 60) * 2.5
      const [wx0, wx1] = worldRangeAtDepth(
        cam.x,
        Z_CLOUD,
        0.35,
        this.viewW,
        this.viewH,
      )
      const tint = theme === 'night' ? 0x101838 : 0xffffff
      const first = Math.floor((wx0 - CLOUD_PERIOD) / CLOUD_PERIOD)
      const last = Math.ceil((wx1 + CLOUD_PERIOD) / CLOUD_PERIOD)
      // Scaled with depth so a distant cloud is genuinely smaller on screen,
      // which is the other half of what "in real 3d space" means.
      const scale = 1 + -Z_CLOUD / 900

      for (let i = first; i <= last; i++) {
        for (let row = 0; row < CLOUD_ROWS; row++) {
          const seed = i * 131 + row * 7
          const key = hash01(seed) < 0.5 ? CLOUD_KEYS[0] : CLOUD_KEYS[1]
          const ox = hash01(seed + 17) * (CLOUD_PERIOD - 40)
          const yy =
            cloudRowY(cam.y, row, CLOUD_ROWS, hash01(seed + 53), this.viewH) -
            Math.sin(drift * 0.004 + i) * 3
          const size = quadSize(key)
          this.cloudBatch(key, tint).add(
            i * CLOUD_PERIOD + ox + drift,
            yy,
            size.w * scale,
            size.h * scale,
            Z_CLOUD,
          )
        }
      }
    }
    for (const batch of this.cloudQuads.values()) batch.end()
  }

  /**
   * The batch for one cloud art key and tint, created on first use.
   *
   * Its material is built here rather than through `quadMaterial`, whose cached
   * quads write depth: clouds must not write depth, or a drifting cloud would
   * punch a hole through the hills behind it.
   */
  private cloudBatch(key: string, tint: number): QuadBatch {
    const signature = `${key}|${tint}`
    let batch = this.cloudQuads.get(signature)
    if (batch === undefined) {
      batch = new QuadBatch(
        this.parallaxMaterial({
          map: spriteTexture(key),
          transparent: true,
          alphaTest: 0.5,
          depthWrite: false,
          color: tint,
        }),
      )
      batch.mesh.renderOrder = -900
      this.clouds.add(batch.mesh)
      this.cloudQuads.set(signature, batch)
    }
    return batch
  }

  /** Hills as real objects, sitting behind and below the play plane. */
  private drawHills(cam: Camera): void {
    for (const batch of this.hillQuads.values()) batch.begin()
    const show = this.backdropTheme === 'overworld'
    if (show) {
      const [wx0, wx1] = worldRangeAtDepth(
        cam.x,
        Z_HILL,
        0.2,
        this.viewW,
        this.viewH,
      )
      const first = Math.floor((wx0 - HILL_PERIOD) / HILL_PERIOD)
      const last = Math.ceil((wx1 + HILL_PERIOD) / HILL_PERIOD)
      const size = quadSize('hill-lg')
      const scale = 1 + -Z_HILL / 700
      for (let i = first; i <= last; i++) {
        for (let k = 0; k < 2; k++) {
          const ox = hash01(900 + k) * (HILL_PERIOD - 48)
          this.hillBatch().add(
            i * HILL_PERIOD + ox,
            this.hillBaseY,
            size.w * scale,
            size.h * scale,
            Z_HILL,
          )
        }
      }
    }
    for (const batch of this.hillQuads.values()) batch.end()
  }

  private hillBatch(): QuadBatch {
    let batch = this.hillQuads.get('hill-lg')
    if (batch === undefined) {
      batch = new QuadBatch(
        this.parallaxMaterial({
          map: spriteTexture('hill-lg'),
          transparent: true,
          alphaTest: 0.5,
          depthWrite: false,
        }),
      )
      batch.mesh.renderOrder = -800
      this.hills.add(batch.mesh)
      this.hillQuads.set('hill-lg', batch)
    }
    return batch
  }

  /**
   * A material for a parallax batch.
   *
   * The parallax layers are the one place flat quads are *not* interchangeable:
   * they draw without depth writes, so they keep their own materials instead of
   * `quadMaterial`'s cached ones. They are owned here, because no shared cache
   * will dispose them.
   */
  private parallaxMaterial(
    params: THREE.MeshBasicMaterialParameters,
  ): THREE.Material {
    const material = new THREE.MeshBasicMaterial(params)
    this.parallaxMaterials.push(material)
    return material
  }

  // ── editor overlay ─────────────────────────────────────────────────────────

  private drawOverlay(cam: Camera, opts: RenderOptions): void {
    const showGrid = opts.showGrid === true
    this.grid.visible = showGrid
    if (showGrid) {
      const pad = this.cullPad
      const tx0 = Math.floor((cam.x - pad.x) / TILE)
      const ty0 = Math.floor((cam.y - pad.y) / TILE)
      const tx1 = Math.floor((cam.x + this.viewW + pad.x) / TILE)
      const ty1 = Math.floor((cam.y + this.viewH + pad.y) / TILE)
      const pts: number[] = []
      for (let tx = tx0; tx <= tx1 + 1; tx++) {
        if (tx % 8 === 0) continue
        const x = tx * TILE
        pts.push(x, -ty0 * TILE, 0, x, -(ty1 + 1) * TILE, 0)
      }
      for (let ty = ty0; ty <= ty1 + 1; ty++) {
        if (ty % 8 === 0) continue
        const y = ty * TILE
        pts.push(tx0 * TILE, -y, 0, (tx1 + 1) * TILE, -y, 0)
      }
      setLinePositions(this.grid, pts)
    }

    const cursor = opts.editorCursor
    if (cursor === null || cursor === undefined) {
      this.cursorBox.visible = false
      this.cursorFill.visible = false
      return
    }
    const w = cursor.w * TILE
    const h = cursor.h * TILE
    const x = cursor.tx * TILE
    const y = cursor.ty * TILE
    const colour = cursor.ok ? 0x00e800 : 0xf80000

    this.cursorFill.visible = true
    this.cursorFill.position.set(x + w / 2, -(y + h / 2), 0)
    this.cursorFill.scale.set(w, h, 1)
    ;(this.cursorFill.material as THREE.MeshBasicMaterial).color.setHex(colour)

    this.cursorBox.visible = true
    ;(this.cursorBox.material as THREE.LineBasicMaterial).color.setHex(colour)
    setLinePositions(this.cursorBox, [
      x,
      -y,
      0,
      x + w,
      -y,
      0,
      x + w,
      -y,
      0,
      x + w,
      -(y + h),
      0,
      x + w,
      -(y + h),
      0,
      x,
      -(y + h),
      0,
      x,
      -(y + h),
      0,
      x,
      -y,
      0,
    ])
  }

  // ── lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Render into a sub-rect of the buffer, or the whole buffer when null.
   *
   * Scissoring matters as much as the viewport: without it the parts of the scene
   * outside the rect would still be rasterised and would show through the
   * editor's opaque chrome.
   */
  setViewport(rect: ViewportRect | null): void {
    this.viewport = rect
    this.applyViewport(rect)
  }

  private applyViewport(rect: ViewportRect | null): void {
    if (rect === null) {
      this.renderer.setViewport(0, 0, VIEW_W, VIEW_H)
      this.renderer.setScissorTest(false)
      return
    }
    // WebGL's viewport origin is bottom-left; the editor counts from the top.
    this.renderer.setViewport(rect.x, VIEW_H - rect.y - rect.h, rect.w, rect.h)
    this.renderer.setScissor(rect.x, VIEW_H - rect.y - rect.h, rect.w, rect.h)
    this.renderer.setScissorTest(true)
  }

  resize(): void {
    this.renderer.setSize(VIEW_W, VIEW_H, false)
    this.applyViewport(this.viewport)
  }

  /**
   * Which world point a pixel of the rendered rect is over.
   *
   * The inverse of what the camera does, and the reason the editor's tile picking
   * is still exact. Screen-to-world used to be arithmetic — `cam + (p - origin)` —
   * which holds only for an orthographic camera. Under a tilted perspective one,
   * a straight line of screen pixels maps to a *curve* of world positions, and
   * the arithmetic drifts by a tile or more toward the bottom of the view.
   *
   * Two things made it invisible. The placement cursor is drawn from the same
   * arithmetic, so the highlight agreed with the hit test and the two were wrong
   * together; and the drift is smooth, so it looked like a slightly-off camera
   * rather than broken picking. The net effect was painting terrain somewhere
   * other than where you clicked.
   *
   * Un-projecting through the real camera has no such failure mode: it is the
   * exact inverse of the projection, for any camera.
   */
  worldAt(px: number, py: number): { x: number; y: number } {
    const ndcX = (px / this.viewW) * 2 - 1
    const ndcY = 1 - (py / this.viewH) * 2
    const ray = new THREE.Vector3(ndcX, ndcY, 0.5).unproject(this.camera)
    // Where that ray crosses the play plane, z = 0.
    const dir = ray.sub(this.camera.position)
    if (dir.z === 0) {
      return { x: this.camera.position.x, y: -this.camera.position.y }
    }
    const t = -this.camera.position.z / dir.z
    const hit = this.camera.position.clone().addScaledVector(dir, t)
    return { x: hit.x, y: -hit.y }
  }

  get drawCalls(): number {
    return this.renderer.info.render.calls
  }

  get triangles(): number {
    return this.renderer.info.render.triangles
  }

  dispose(): void {
    this.backdropTexture?.dispose()
    // Batches are ordinary children of the scene, so this disposes their
    // geometry and their shared materials with everything else.
    disposeTree(this.scene)
    this.pools.clear()
    this.terrainQuads.clear()
    this.terrainModels.clear()
    this.cloudQuads.clear()
    this.hillQuads.clear()
    this.objectQuads.clear()
    for (const mat of this.parallaxMaterials) mat.dispose()
    this.parallaxMaterials.length = 0
    for (const mat of this.materials.values()) mat.dispose()
    this.materials.clear()
    for (const geo of this.geometries.values()) geo.dispose()
    this.geometries.clear()
    this.renderer.dispose()
  }
}

// ── helpers ──────────────────────────────────────────────────────────────────

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

function makeLineSegments(colour: number, opacity: number): THREE.LineSegments {
  const geo = new THREE.BufferGeometry()
  geo.setAttribute(
    'position',
    new THREE.BufferAttribute(new Float32Array(3 * 2), 3),
  )
  const mat = new THREE.LineBasicMaterial({
    color: colour,
    transparent: opacity < 1,
    opacity,
    depthTest: false,
  })
  const lines = new THREE.LineSegments(geo, mat)
  lines.frustumCulled = false
  lines.renderOrder = Z_OVERLAY
  return lines
}

function setLinePositions(lines: THREE.LineSegments, flat: number[]): void {
  let attr = lines.geometry.getAttribute('position') as THREE.BufferAttribute
  if (attr.array.length < flat.length) {
    // Replacing the attribute updates its vertex count and GPU allocation.
    attr = new THREE.BufferAttribute(new Float32Array(flat.length), 3)
    lines.geometry.setAttribute('position', attr)
  }
  attr.array.set(flat)
  attr.needsUpdate = true
  lines.geometry.setDrawRange(0, flat.length / 3)
  lines.visible = flat.length > 0
}

function disposeTree(node: THREE.Object3D): void {
  node.traverse((child) => {
    const mesh = child as THREE.Mesh
    // An instanced mesh's buffers are not geometry attributes, so only its own
    // dispose event frees them; without this an HMR replacement would leak one
    // instance buffer per terrain batch.
    if ((mesh as THREE.InstancedMesh).isInstancedMesh === true) {
      ;(mesh as THREE.InstancedMesh).dispose()
    }
    if (mesh.geometry !== undefined) mesh.geometry.dispose()
    if (mesh.material !== undefined) {
      const materials = Array.isArray(mesh.material)
        ? mesh.material
        : [mesh.material]
      for (const material of materials) material.dispose()
    }
  })
}
